import { z } from 'zod';
import type { FilterCondition, FilterConfig, FilterMode } from './filters';

export const savedSearchEntityTypeSchema = z.enum(['contacts', 'prospects', 'crews']);
export type SavedSearchEntityType = z.infer<typeof savedSearchEntityTypeSchema>;

const filterOperatorSchema = z.enum([
  'equals',
  'notEquals',
  'notEqualsOrNull',
  'contains',
  'notContains',
  'startsWith',
  'notStartsWith',
  'endsWith',
  'notEndsWith',
  'greaterThan',
  'lessThan',
  'before',
  'after',
  'between',
  'sameDay',
  'isNotNull',
  'isNull',
]);

const savedFilterSchema = z.object({
  field: z.string().min(1),
  operator: filterOperatorSchema,
  value: z.union([z.string(), z.array(z.string())]),
  label: z.string().optional(),
});

const savedSortSchema = z.object({
  field: z.string().min(1),
  direction: z.enum(['asc', 'desc']),
});

export const savedSearchDefinitionSchema = z.object({
  searchTerm: z.string().max(500),
  filterMode: z.enum(['and', 'or']),
  filters: z.array(savedFilterSchema).max(50),
  requiredFilters: z.array(savedFilterSchema).max(20),
  sort: savedSortSchema.nullable(),
  columnVisibility: z.record(z.string(), z.boolean()),
  pageSize: z.number().int().min(1).max(100),
});

export type SavedSearchDefinition = z.infer<typeof savedSearchDefinitionSchema>;

export const savedSearchSchema = z.object({
  id: z.string().uuid(),
  owner_user_id: z.string().uuid(),
  entity_type: savedSearchEntityTypeSchema,
  name: z.string(),
  is_shared: z.boolean(),
  definition: savedSearchDefinitionSchema,
  definition_version: z.literal(1),
  created_at: z.string(),
  updated_at: z.string(),
});

export type SavedSearch = z.infer<typeof savedSearchSchema>;

export interface SavedSearchInput {
  name: string;
  isShared: boolean;
  definition: SavedSearchDefinition;
}

export interface SanitizedSavedSearchDefinition {
  definition: SavedSearchDefinition;
  ignoredFilters: string[];
}

export function sanitizeSavedSearchDefinition(
  definition: SavedSearchDefinition,
  filterConfigs: FilterConfig[]
): SanitizedSavedSearchDefinition {
  const configsByField = new Map(filterConfigs.map((config) => [config.field, config]));
  const ignoredFilters: string[] = [];
  const filters = definition.filters.filter((filter) => {
    const config = configsByField.get(filter.field);
    const isValid = config?.operators.includes(filter.operator) ?? false;
    if (!isValid) ignoredFilters.push(filter.label || filter.field);
    return isValid;
  });

  return {
    definition: { ...definition, filters },
    ignoredFilters,
  };
}

export function createSavedSearchDefinition(params: {
  searchTerm: string;
  filters: FilterCondition[];
  requiredFilters?: FilterCondition[];
  filterMode: FilterMode;
  sortField: string | null;
  sortDirection: 'asc' | 'desc';
  columnVisibility: Record<string, boolean>;
  pageSize: number;
}): SavedSearchDefinition {
  return savedSearchDefinitionSchema.parse({
    searchTerm: params.searchTerm,
    filters: params.filters,
    requiredFilters: params.requiredFilters || [],
    filterMode: params.filterMode,
    sort: params.sortField ? { field: params.sortField, direction: params.sortDirection } : null,
    columnVisibility: params.columnVisibility,
    pageSize: params.pageSize,
  });
}

export function getSavedSearchSignature(definition: SavedSearchDefinition): string {
  return JSON.stringify({
    ...definition,
    columnVisibility: Object.fromEntries(
      Object.entries(definition.columnVisibility).sort(([left], [right]) =>
        left.localeCompare(right)
      )
    ),
  });
}
